import React, { useContext, useMemo, useState } from 'react';
import { Modal } from '@stormwater-software/lib-ui';
import NotificationContext from '../../../contexts/NotificationsContext';
import { useOffline } from '../../../contexts/OfflineContext';
import { INotification } from '../../../hooks/useNotificationNavigate';
import NotificationList from './NotificationList';

export interface INotificationsModalProps {
  show: boolean;
  handleClose: () => void;
  onSelect: (notification: INotification) => void;
}

const ALL_TENANTS = 'all';

/**
 * The expanded notification view (SW2-1651). The same rows as the popper, with
 * room to filter by tenant — which matters for the users who belong to dozens.
 *
 * Filtering runs over the data already in the react-query cache, so opening this
 * costs no request.
 */
const NotificationsModal: React.FC<INotificationsModalProps> = ({
  show,
  handleClose,
  onSelect,
}) => {
  const notificationContext = useContext(NotificationContext);
  const isOffline = useOffline();

  const [tenantFilter, setTenantFilter] = useState<string>(ALL_TENANTS);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const notifications: INotification[] = notificationContext.notifications;

  /** The tenants actually represented in the list, not every tenant the user has */
  const tenantOptions = useMemo(() => {
    const nameById: Record<string, string> = {};

    notifications.forEach((notification) => {
      if (notification.tenantId && notification.tenantName) {
        nameById[String(notification.tenantId)] = notification.tenantName;
      }
    });

    return Object.keys(nameById)
      .map((id) => ({ id, name: nameById[id] }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [notifications]);

  const visible = useMemo(() => {
    return notifications.filter((notification) => {
      if (unreadOnly && notification.is_opened) {
        return false;
      }

      if (tenantFilter === ALL_TENANTS) {
        return true;
      }

      return String(notification.tenantId) === tenantFilter;
    });
  }, [notifications, tenantFilter, unreadOnly]);

  const handleSelect = (notification: INotification) => {
    onSelect(notification);
    handleClose();
  };

  const handleMarkAllRead = () => {
    setErrorMsg('');

    notificationContext
      .markNotificationAsRead()
      .catch(() => setErrorMsg('Could not mark notifications as read.'));
  };

  return (
    <Modal
      show={show}
      isWide
      title='Notifications'
      classes='notifications-modal'
      errorMsg={errorMsg}
      dismissOnBgClick
      dismissOnEsc
      cancelBtn
      cancelBtnText='Close'
      submitBtnText='Mark all read'
      disableSubmit={isOffline || !notifications.length}
      handleClose={handleClose}
      handleSubmit={handleMarkAllRead}
    >
      <div className='notifications-modal-filters'>
        <label>
          Tenant
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            disabled={!tenantOptions.length}
          >
            <option value={ALL_TENANTS}>All tenants</option>
            {tenantOptions.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </label>

        <label className='notifications-modal-unread'>
          <input
            type='checkbox'
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          Unread only
        </label>
      </div>

      <NotificationList
        notifications={visible}
        onSelect={handleSelect}
        variant='expanded'
        emptyMessage={
          notifications.length
            ? 'No notifications match this filter.'
            : 'You have no notifications.'
        }
      />
    </Modal>
  );
};

export default NotificationsModal;
